import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/api/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function SignInApproval() {
  const [params] = useSearchParams();
  const id = params.get('id');
  const [request, setRequest] = useState(null);
  const [number, setNumber] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    let stopped = false;
    setRequest(null); setError(''); setNumber(''); setExpired(false);
    if (!id) { setError('No sign-in request was supplied.'); return; }
    api.request('GET', `/auth/2fa/push/${encodeURIComponent(id)}`)
      .then(data => { if (!stopped) setRequest(data); })
      .catch(err => { if (!stopped) setError(err.message); });
    return () => { stopped = true; };
  }, [id]);
  useEffect(() => {
    if (!request) return;
    const timer = setTimeout(() => setExpired(true), Math.max(0, new Date(request.expires_at).getTime() - Date.now()));
    return () => clearTimeout(timer);
  }, [request]);

  async function decide(decision) {
    setBusy(true); setError('');
    try {
      const data = await api.request('POST', `/auth/2fa/push/${encodeURIComponent(id)}/decision`, { decision, match_number: number });
      setRequest(current => ({ ...current, status: data.status }));
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  return <main className="max-w-md mx-auto p-6 space-y-5">
    <h1 className="text-2xl font-semibold">Approve sign-in</h1>
    {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
    {!request && !error && <p role="status">Loading request…</p>}
    {request?.status === 'pending' && !expired && <>
      <p>Only approve if you are signing in on another device right now.</p>
      <div className="rounded-lg border p-3 text-sm space-y-2">
        <p>Requested {new Date(request.created_at).toLocaleString()}</p>
        <p className="break-words text-gray-500">Device: {request.device}</p>
      </div>
      <form className="space-y-4" onSubmit={event => { event.preventDefault(); decide('approve'); }}>
        <div className="space-y-2">
          <Label htmlFor="matching-number">Number shown on the device signing in</Label>
          <Input id="matching-number" inputMode="numeric" autoComplete="off" pattern="[0-9]{3}" maxLength={3} required value={number} onChange={event => setNumber(event.target.value.replace(/\D/g, ''))} />
        </div>
        <Button type="submit" className="w-full" disabled={busy || number.length !== 3}>Approve sign-in</Button>
        <Button type="button" variant="outline" className="w-full" disabled={busy} onClick={() => decide('deny')}>Deny — this wasn’t me</Button>
      </form>
    </>}
    {request && (request.status !== 'pending' || expired) && <div role="status" className="space-y-3">
      <p>{request.status === 'approved' || request.status === 'consumed' ? 'Request completed. You can return to the device you were signing in on.' : request.status === 'denied' ? 'Sign-in denied. If you didn’t make this request, change your password.' : 'This sign-in request has expired. Start again on the other device.'}</p>
      <Link className="underline" to="/profile">Go to my profile</Link>
    </div>}
  </main>;
}
